// schema-sidebar.tsx — opt-in side rail listing payload fields with
// occurrence frequency. Click toggles an `is_present` filter on the
// active recipe. Extracted from DataSurfaceV3.tsx.
//
// Operator can flip this on via the toolbar 'schema panel' checkbox.
// The auto-projected payload columns already act as discovery so the
// sidebar is mostly used to add quick presence filters.

import React from 'react';
import type { SchemaField } from '../../api';
import type { FilterRow } from './data-surface-utils';

export function SchemaSidebar({ fields, activeFilters, onToggleFilter }: {
  fields: SchemaField[];
  activeFilters: FilterRow[];
  onToggleFilter: (path: string) => void;
}): React.ReactElement {
  // Compute the "max occurrence" for relative-frequency styling.
  // Fields present in 100% of sampled events render bold; sparser
  // fields fade. Without this, a CSV upload where every field is
  // uniformly present looks identically populated — visual noise
  // instead of signal.
  const maxOcc = Math.max(1, ...fields.map(f => f.occurrences));
  return (
    <div style={{ background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'stretch', maxHeight: 540, overflowY: 'auto' }}>
      <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
        Fields in your data
      </div>
      {fields.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>No fields detected yet. Send events to populate.</div>}
      {fields.map(f => {
        const active = activeFilters.some(x => x.field_path === f.path && x.op === 'is_present');
        const ratio = f.occurrences / maxOcc;
        const opacity = ratio >= 0.5 ? 1 : Math.max(0.55, ratio + 0.3);
        return (
          <button
            key={f.path}
            type="button"
            onClick={() => onToggleFilter(f.path)}
            title={`${f.types.join(' | ')} · ${f.occurrences}× · samples: ${f.sample_values.slice(0, 3).map(v => JSON.stringify(v)).join(', ')}\n\nClick to ${active ? 'remove' : 'add'} "is present" filter.`}
            style={{
              textAlign: 'left', padding: '4px 8px',
              background: active ? 'var(--accent-soft)' : 'var(--bg)',
              border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line)'}`,
              borderRadius: 4, cursor: 'pointer',
              opacity,
            }}
          >
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink)', display: 'flex', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: ratio >= 0.95 ? 500 : 400 }}>{f.path}</span>
              <span style={{ color: 'var(--ink-3)', flexShrink: 0, fontSize: 10.5 }}>{Math.round(ratio * 100)}%</span>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--ink-3)' }}>{f.types.join(', ')}</div>
          </button>
        );
      })}
    </div>
  );
}
